import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { Analytics } from '@vercel/analytics/react';
import Navbar from './components/Navbar';
import Events from './pages/Events';
import Profile from './pages/Profile';
import UserProfile from './pages/UserProfile';
import EventDetail from './pages/EventDetail';
import MyEvents from './pages/MyEvents';
import Inbox from './pages/Inbox';
import Chat from './pages/Chat';
import LikedEvents from './pages/LikedEvents';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Footer from './components/Footer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#533B7D',
      light: '#E8DDFF',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#533B7D',
      light: '#E8DDFF',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1.125rem',
      },
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#333333',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
          borderRadius: 12,
          border: '1px solid #EBEBEB',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
          border: '1px solid #EBEBEB',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
          border: '1px solid #EBEBEB',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
      },
    },
  },
});

// Main App Content Component
const AppContent: React.FC = () => {
  const [searchSport, setSearchSport] = useState<string>('');
  const [refreshEvents, setRefreshEvents] = useState<number>(0);
  const [triggerSignup, setTriggerSignup] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleUnauthenticated = () => {
    setTriggerSignup(true);
  };

  const handleSearchChange = (sport: string) => {
    setSearchSport(sport);
  };

  const handleEventCreated = () => {
    // Trigger events refresh by updating the refresh counter
    setRefreshEvents(prev => prev + 1);
  };

  const { isAuthenticated, loading } = useAuth();

  const handleEventClick = (eventId: string) => {
    if (!isAuthenticated) {
      // Trigger signup modal if user is not authenticated
      setTriggerSignup(true);
      return;
    }
    navigate(`/event/${eventId}`);
  };

  const handleSignupTriggered = () => {
    setTriggerSignup(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleInboxClick = () => {
    navigate('/inbox');
  };

  const handleBackToEvents = () => {
    navigate('/');
  };

  if (loading) {
    return null;
  }

  const hideFooter = ['/chat', '/inbox'].includes(location.pathname);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar
        onSearchChange={handleSearchChange}
        onEventCreated={handleEventCreated}
        onProfileClick={handleProfileClick}
        onInboxClick={handleInboxClick}
        triggerSignup={triggerSignup}
        onSignupTriggered={handleSignupTriggered}
      />
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Routes>
          <Route
            path="/"
            element={
              <Events
                searchSport={searchSport}
                refreshTrigger={refreshEvents}
                onEventClick={handleEventClick}
              />
            }
          />
          <Route path="/profile" element={<ProtectedRoute onUnauthenticated={handleUnauthenticated}><Profile /></ProtectedRoute>} />
          <Route path="/user/:userId" element={<UserProfile />} />
          <Route path="/my-events" element={<ProtectedRoute onUnauthenticated={handleUnauthenticated}><MyEvents /></ProtectedRoute>} />
          <Route path="/inbox" element={<ProtectedRoute onUnauthenticated={handleUnauthenticated}><Inbox /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute onUnauthenticated={handleUnauthenticated}><Chat /></ProtectedRoute>} />
          <Route path="/liked-events" element={<ProtectedRoute onUnauthenticated={handleUnauthenticated}><LikedEvents /></ProtectedRoute>} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route
            path="/event/:eventId"
            element={<EventDetailWrapper onBack={handleBackToEvents} onUnauthenticated={handleUnauthenticated} />}
          />
        </Routes>
      </Box>
      {!hideFooter && <Footer />}
    </div>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode; onUnauthenticated?: () => void }> = ({ children, onUnauthenticated }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isAuthenticated) {
      if (onUnauthenticated) onUnauthenticated();
      navigate('/');
    }
  }, [isAuthenticated, navigate, onUnauthenticated]);

  if (!isAuthenticated) return null;
  return <>{children}</>;
};

const EventDetailWrapper: React.FC<{ onBack: () => void; onUnauthenticated?: () => void }> = ({ onBack, onUnauthenticated }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isAuthenticated) {
      if (onUnauthenticated) onUnauthenticated();
      navigate('/');
    }
  }, [isAuthenticated, navigate, onUnauthenticated]);

  if (!eventId || !isAuthenticated) return null;

  return <EventDetail eventId={eventId} onBack={onBack} />;
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <AppContent />
          </Router>
          <Analytics />
        </ThemeProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
